"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";

const BP = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

interface ShowResult {
  id: string;
  slug: string;
  year: number;
  season: string;
  line?: string | null;
  brand: { name: string; slug: string };
  directors: { name: string }[];
  _count: { images: number };
}

function SearchForm() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [results, setResults] = useState<ShowResult[] | null>(null);
  const [loading, setLoading] = useState(false);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    router.replace(`/search?q=${encodeURIComponent(q)}`, { scroll: false });
    try {
      const res = await fetch(`${BP}/api/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(data.results ?? []);
    } finally {
      setLoading(false);
    }
  }, [router]);

  return (
    <div>
      {/* Input */}
      <div style={{ display: "flex", gap: 8, marginBottom: 48 }}>
        <input
          className="ws-input"
          style={{ flex: 1, fontSize: 17 }}
          placeholder="Brand, director, year, season…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runSearch(query)}
          autoFocus
        />
        <button className="ws-btn primary" onClick={() => runSearch(query)} disabled={loading}>
          {loading ? "…" : "Search"}
        </button>
      </div>

      {/* Empty state hint */}
      {results === null && !loading && (
        <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 20 }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 12 }}>
            Try searching for
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["Chanel", "Dior", "2023", "Fall", "Raf Simons", "Prada"].map(hint => (
              <button
                key={hint}
                className="filter-tab"
                onClick={() => { setQuery(hint); runSearch(hint); }}
              >
                {hint}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {results !== null && results.length === 0 && (
        <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 20 }}>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-3)" }}>
            No results for &ldquo;{query}&rdquo;
          </p>
        </div>
      )}

      {/* Results */}
      {results && results.length > 0 && (
        <div>
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>
              {results.length} result{results.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div style={{ border: "1px solid var(--ink)" }}>
            {results.map((show, i) => (
              <Link key={show.id} href={`/archive/${show.brand.slug}/${show.slug}`} style={{ textDecoration: "none" }}>
                <div className="search-result-row" style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 180px 80px",
                  padding: "20px 24px",
                  borderBottom: i < results.length - 1 ? "1px solid var(--paper-2)" : "none",
                  gap: 16,
                  alignItems: "center",
                  background: "var(--paper)",
                  transition: "background 0.1s",
                }}>
                  <div>
                    <p style={{ fontFamily: "var(--font-display)", fontSize: 22, letterSpacing: "-0.02em", lineHeight: 1.1, color: "var(--ink)" }}>
                      {show.brand.name}
                    </p>
                    {show.directors.length > 0 && (
                      <p style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-3)", marginTop: 5 }}>
                        {show.directors.map(d => d.name).join(" & ")}
                      </p>
                    )}
                  </div>
                  <div>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-2)", display: "block" }}>
                      {show.season} {show.year}
                    </span>
                    {show.line && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-3)", display: "block", marginTop: 3 }}>
                        {show.line.startsWith(show.brand.name + " ") ? show.line.slice(show.brand.name.length + 1) : show.line}
                      </span>
                    )}
                  </div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-3)", textAlign: "right" }}>
                    {show._count.images} looks
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SearchClient() {
  return (
    <Suspense fallback={null}>
      <SearchForm />
    </Suspense>
  );
}
